import { CampusIcon } from "@cmp-src/icons/TacticIcons/CampusIcon";
import { DotIcon } from "@cmp-src/icons/TacticIcons/DotIcon";
import { cn, truncateOfficeAndFloor } from "@gettactic/helpers";
import { useIsMobile } from "@v2/hooks/use-mobile";
import React from "react";

export type ResourceLocationProps = {
  location?: string;
  floor?: string;
  area?: string;
  withIcon?: boolean;
  className?: string;
  showDividers?: boolean;
  maxLength?: number;
};
const Segment: React.FC<{
  children?: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
}> = ({ children, icon, className }) => {
  if (!children) return null;

  return (
    <div className={cn("flex flex-shrink-0 items-center gap-1", className)}>
      {icon && (
        <span aria-hidden className="inline-flex flex-shrink-0 items-center">
          {icon}
        </span>
      )}
      <span className="whitespace-nowrap">{children}</span>
    </div>
  );
};
export const ResourceLocation = React.memo(function ResourceLocation({
  location = "Office",
  floor,
  area,
  withIcon = true,
  className,
  showDividers = true,
  maxLength: maxLengthProp = 45
}: ResourceLocationProps) {
  const isMobile = useIsMobile();
  const maxLength = isMobile ? maxLengthProp * 1.5 : maxLengthProp;
  const truncated = React.useMemo(
    () => truncateOfficeAndFloor(location || "", floor || "", area, maxLength),
    [location, floor, area, maxLength]
  );

  // mobile: keep single compact row with leading campus icon and location text
  if (isMobile) {
    return (
      <div
        className={cn(
          "flex items-center gap-2 text-xs",
          "overflow-hidden",
          className
        )}
        aria-label="Resource location"
      >
        {withIcon && truncated.officeName && (
          <span aria-hidden className="flex-shrink-0">
            <CampusIcon />
          </span>
        )}
        {truncated.officeName && (
          <span className="truncate">{truncated.officeName}</span>
        )}
      </div>
    );
  }

  // desktop: compact left-aligned segments with intelligent truncation
  return (
    <div
      className={cn(
        "flex items-center gap-1 overflow-hidden text-xs",
        className
      )}
      aria-label="Resource location"
    >
      <Segment icon={withIcon ? <CampusIcon /> : null}>
        <span className="font-medium">{truncated.officeName}</span>
      </Segment>

      {truncated.floorName && (
        <Segment icon={<DotIcon />}>
          <span className="font-medium">{truncated.floorName}</span>
        </Segment>
      )}

      {truncated.areaName && (
        <Segment icon={showDividers ? <DotIcon /> : null}>
          <span className="font-medium">{truncated.areaName}</span>
        </Segment>
      )}
    </div>
  );
});
